import { createSlice } from "@reduxjs/toolkit";
import { changePolicy, fullLogout } from "globals/misc";
import { clearPremiumCache } from "utils/premiumCalculationCache";

const defaultState = {
	sideBarInfo: {
		mode: "",
		quicktip: { externalContent: "", quicktipContainerContent: "", styling: "" }
	},
	progressLine: {
		enabled: false,
		percentage: 0,
	},
	previousNavigationLink: {
		pageName: "",
		link: "",
		selectedPolicyEditorTab: {
			mainFlowIndex: null,
			subFlowIndex: null
		}
	},
	payBalanceUserProgress: true,
	missingDocuments: [],
	notification: [], 
	clearNotificationStatus: false,
	userData: {},
	currentUser: {},
	policyHolders: [],
	isLoggedIn: false,
	userId: "",
	token: "",
	refreshToken: "",
	redirectTarget: "/",
	policies: [],
	promoCodes: [],
	renewablePolicies: [],
	extendablePolicies: [],
	coverNotePolicies: [],
	fetchExtendableSuccessfully: false,
	activePolicy: {},
	oldPolicy: {},
	activePolicies: [],
	activeRisk: {},
	breakdown: {},
	otherCalculations: {},
	authFormInfo: {
		TRN: null,
		DOB: null,
		gender: null,
	},
	stickyNavHeaderVisible: null,
	valuationStatus: null,
	halfPayment: {},
	paymentPlans: [],
	paymentType: {},
	quoteResponse: null,
	persistUser: false,
	transactionType: null,
	policyPaymentPlan: {},
	missingUserData: [],
	noLossDeclarationStatus: false,
	hasMotorPolicies: false,
	hasHomePolicies: false,
	hasLiabilityPolicies: false,
	selectedSession: {
		isSessionCreated: false,
		sessionId: null
	},
	isOtherDeviceLogin: false,
	globalNarratives: {}
};

let authSlice = createSlice({
	name: "auth",
	initialState: defaultState,
	reducers: {
		login: state => {
			state.isLoggedIn = true;
		},
		setProgressLine: (state, action) => {
			state.progressLine = action.payload;
		},
		setGlobalNarratives: (state, action) => {
			state.globalNarratives = action.payload;
		},   
		setNotification: (state, action) => {
			state.notification = action.payload;
		},  
		setClearNotificationStatus: (state, action) => {
			state.clearNotificationStatus = action.payload;
		},
		setSideBarInfo: (state, action) => {
			state.sideBarInfo = action.payload;
		},
		setPayBalanceUserProgress: (state, action) => {
			state.payBalanceUserProgress = action.payload;
		},
		setPreviousNavigationLink: (state, action) => {
			state.previousNavigationLink = action.payload;
		},
		setMissingDocuments: (state, action) => {
			state.missingDocuments = action.payload;
		},
		setToken: (state, action) => {
			state.token = action.payload;
			try {
				require("../../api/Middleware").storeAccessToken(action.payload);
			} catch {
				// Best-effort local fallback for legacy CORS mode.
			}
		},
		setRefreshToken: (state, action) => {
			state.refreshToken = action.payload;
			try {
				require("../../api/Middleware").storeRefreshToken(action.payload);
			} catch {
				// Best-effort local fallback for legacy CORS mode.
			}
		},
		setStickyNavHeaderVisible: (state, action) => {
			state.stickyNavHeaderVisible = action.payload;
		},
		setRedirectTarget: (state, action) => {
			state.redirectTarget = action.payload;
		},
		setTransactionType: (state, action) => {
			state.transactionType = action.payload;
		},
		setValuationStatus: (state, action) => {
			state.valuationStatus = action.payload;
		},
		setUserFormData: (state, action) => {
			state.userData = action.payload;
		},
		setExtendablePolicies: (state, action) => {
			state.extendablePolicies = action.payload;
		},
		setCurrentUser: (state, action) => {
			state.currentUser = action.payload;
		},
		setPolicyHolders: (state, action) => {
			state.policyHolders = action.payload;
		},

		setExtendablePoliciesFetched: (state, action) => {
			state.fetchExtendableSuccessfully = action.payload;
		},
		setPaymentType: (state, action) => {
			state.paymentType = action.payload;
		},
		setQuoteResponse: (state, action) => {
			state.quoteResponse = action.payload;
		  },
		setUserId: (state, action) => {
			state.userId = action.payload;
		},
		setPolicies: (state, action) => {
			state.policies = action.payload;
		},
		setActivePolicies: (state, action) => {
			state.activePolicies = action.payload;
		},
		setRenewablePolicies: (state, action) => {
			state.renewablePolicies = action.payload;
		},

		setCoverNotePolicies: (state, action) => {
			state.coverNotePolicies = action.payload;
		},

		setActivePolicy: (state, action) => {
			state.activePolicy = action.payload;
		},

		setOldPolicy: (state, action) => {
			state.oldPolicy = action.payload;
		},

		setActiveRisk: (state, action) => {
			state.activeRisk = action.payload;
		},

		setBreakdown: (state, action) => {
			state.breakdown = action.payload;
		},
		setOtherCalculations: (state, action) => {
			state.otherCalculations = action.payload;
		},
		setAuthFormInfo: (state, action) => {
			state.authFormInfo = action.payload;
		},
		setHalfPayment: (state, action) => {
			state.halfPayment = action.payload;
		},
		setPaymentPlans: (state, action) => {
			state.paymentPlans = action.payload;
		},
		addPromoCode: (state, action) => {
			state.promoCodes.push(action.payload);
		},
		removePromoCodeByIndex: (state, action) => {
			state.promoCodes.splice(action.payload, 1);
		},
		setPersistUser: (state, action) => {
			state.persistUser = action.payload;
		},
		setPolicyPaymentPlan: (state, action) => {
			state.policyPaymentPlan = action.payload;
		},
		setMissingUserData: (state, action) => {
			state.missingUserData = action.payload;
		},
		setNoLossDeclarationStatus: (state, action) => {
			state.noLossDeclarationStatus = action.payload;
		},
		setHasMotorPolicies: (state, action) => {
			state.hasMotorPolicies = action.payload;
		},
		setHasHomePolicies: (state, action) => {
			state.hasHomePolicies = action.payload;
		},
		setHasLiabilitiesPolicies: (state, action) => {
			state.hasLiabilityPolicies = action.payload;
		},
		setSelectedSession: (state, action) => {
			state.selectedSession = action.payload;
		},
		setIsOtherDeviceLogin: (state, action) => {
			state.isOtherDeviceLogin = action.payload;
		},
		logout: () => {
			// Clear premium cache on logout
			clearPremiumCache();
			try {
				require("../../api/Middleware").storeAccessToken("");
				require("../../api/Middleware").storeRefreshToken("");
			} catch {
				// Best-effort local fallback for legacy CORS mode.
			}
			return defaultState;
		},
	},
	extraReducers: builder => {
		builder.addCase(fullLogout, () => {
			// Also clear cache on full logout
			clearPremiumCache();
			try {
				require("../../api/Middleware").storeAccessToken("");
				require("../../api/Middleware").storeRefreshToken("");
			} catch {
				// Best-effort local fallback for legacy CORS mode.
			}
			return defaultState;
		});
		builder.addCase(changePolicy, (state) => {
			state.breakdown = defaultState.breakdown;
			state.otherCalculations = defaultState.otherCalculations;
			state.halfPayment = defaultState.halfPayment;
			state.paymentPlans = defaultState.paymentPlans;
			state.paymentType = defaultState.paymentType;
		});
	},
});



/** @param {{auth: defaultState}} state */
export const getSideBarInfo = ({ auth: state }) => state.sideBarInfo;

/** @param {{auth: defaultState}} state */
export const getProgressLine = ({ auth: state }) => state.progressLine;

/** @param {{auth: defaultState}} state */
export const getGlobalNarratives = ({ auth: state }) => state.globalNarratives;

/** @param {{auth: defaultState}} state */
export const getUser = ({ auth: state }) => state.currentUser;

/** @param {{auth: defaultState}} state */
export const getPolicyHolders = ({ auth: state }) => state.policyHolders;

/** @param {{auth: defaultState}} state */
export const getNotification = ({ auth: state }) => state.notification;

/** @param {{auth: defaultState}} state */
export const getClearNotificationStatus = ({ auth: state }) => state.clearNotificationStatus;

/** @param {{auth: defaultState}} state */
export const getPreviousNavigationLink = ({ auth: state }) => state.previousNavigationLink;

/** @param {{auth: defaultState}} state */
export const getPayBalanceUserProgress = ({ auth: state }) => state.payBalanceUserProgress;

/** @param {{auth: defaultState}} state */
export const getMissingDocuments = ({ auth: state }) => state.missingDocuments;

/** @param {{auth: defaultState}} state */
export const getUserFormData = ({ auth: state }) => state.userData;

/** @param {{auth: defaultState}} state */
export const didFetchExtendablePolices = ({ auth: state }) => state.fetchExtendableSuccessfully;

/** @param {{auth: defaultState}} state */
export const getAuthState = ({ auth: state }) => state.isLoggedIn;

/** @param {{auth: defaultState}} state */
export const getStickyNavHeaderVisible = ({ auth: state }) => state.stickyNavHeaderVisible;

/** @param {{auth: defaultState}} state */
export const getToken = ({ auth: state }) => state.token;

/** @param {{auth: defaultState}} state */
export const getRefreshToken = ({ auth: state }) => state.refreshToken;

/** @param {{auth: defaultState}} state */
export const getRedirectTarget = ({ auth: state }) => state.redirectTarget;

/** @param {{auth: defaultState}} state */
export const getUserId = ({ auth: state }) => state.userId;

/** @param {{auth: defaultState}} state */
export const getPolicies = ({ auth: state }) => state.policies;

/** @param {{auth: defaultState}} state */
export const getPaymentType = ({ auth: state }) => state.paymentType;

export const getQuoteResponse = ({ auth: state }) => state.quoteResponse;

/** @param {{auth: defaultState}} state */
export const getRenewablePolicies = ({ auth: state }) => state.renewablePolicies;

/** @param {{auth: defaultState}} state */
export const getCoverNotePolicies = ({ auth: state }) => state.coverNotePolicies;

/** @param {{auth: defaultState}} state */
export const getExtendablePolicies = ({ auth: state }) => state.extendablePolicies;

/** @param {{auth: defaultState}} state */
export const getValuationStatus = ({ auth: state }) => state.valuationStatus;

/** @param {{auth: defaultState}} state */
export const getTransactionType = ({ auth: state }) => state.transactionType;

/** @param {{auth: defaultState}} state */
export const getActivePolicy = ({ auth: state }) => state.activePolicy;

/** @param {{auth: defaultState}} state */
export const getOldPolicy = ({ auth: state }) => state.oldPolicy;

/** @param {{auth: defaultState}} state */
export const getActivePolicies = ({ auth: state }) => state.activePolicies;

/** @param {{auth: defaultState}} state */
export const getActiveRisk = ({ auth: state }) => state.activeRisk;

/** @param {{auth: defaultState}} state */
export const getBreakdown = ({ auth: state }) => state.breakdown;

/** @param {{auth: defaultState}} state */
export const getPromoCodesApplied = ({ auth: state }) => state.promoCodes;

/** @param {{auth: defaultState}} state */
export const getOtherCalculations = ({ auth: state }) => state.otherCalculations;

/** @param {{auth: defaultState}} state */
export const getAuthFormInfo = ({ auth: state }) => state.authFormInfo;

/** @param {{auth: defaultState}} state */
export const getHalfPayment = ({ auth: state }) => state.halfPayment;

/** @param {{auth: defaultState}} state */
export const getPaymentPlans = ({ auth: state }) => state.paymentPlans;

/** @param {{auth: defaultState}} state */
export const getPolicyPaymentPlan = ({ auth: state }) => state.policyPaymentPlan;

/** @param {{auth: defaultState}} state */
export const getMissingUserData = ({ auth: state }) => state.missingUserData;

/** @param {{auth: defaultState}} state */
export const getNoLossDeclarationStatus = ({ auth: state }) => state.noLossDeclarationStatus;

/** @param {{auth: defaultState}} state */
export const getHasMotorPolicies = ({ auth: state }) => state.hasMotorPolicies;

/** @param {{auth: defaultState}} state */
export const getHasHomePolicies = ({ auth: state }) => state.hasHomePolicies;

/** @param {{auth: defaultState}} state */
export const getHasLiabilitiesPolicies = ({ auth: state }) => state.hasLiabilityPolicies;

/** @param {{auth: defaultState}} state */
export const getSelectedSession = ({ auth: state }) => state.selectedSession;

/** @param {{auth: defaultState}} state */
export const getIsOtherDeviceLogin = ({ auth: state }) => state.isOtherDeviceLogin;

/** 
 * Transforms missingDocuments and policy data into DocumentUploadSection requirements
 * @param {{auth: defaultState}} state 
 */
export const selectUploadRequirements = (state) => {
	const policy = getActivePolicy(state);
	const activeRisk = getActiveRisk(state);
	
	if (!policy?.policy_prefix) {
		return { personal: [], vehicle: [], optional: [] };
	}

	const policyPrefix = policy.policy_prefix;
	const hasMortgagee = activeRisk?.mortgagees && activeRisk.mortgagees.length > 0;

	const rawRegisteredValue = activeRisk?.registered_in_name ??
		activeRisk?.vehicle_registered_in_name ??
		activeRisk?.vehicleRegisteredInName ??
		activeRisk?.registeredToInsured ??
		activeRisk?.is_vehicle_registered_in_name;

	const normalizedRegisteredValue = typeof rawRegisteredValue === 'string'
		? rawRegisteredValue.trim().toLowerCase()
		: rawRegisteredValue;

	const isRegisteredInName = normalizedRegisteredValue === true
		|| normalizedRegisteredValue === 'yes'
		|| normalizedRegisteredValue === 'y'
		|| normalizedRegisteredValue === 'true';

	// Comprehensive policy prefixes
	const comprehensivePrefixes = ['MPCC', 'MPCCS', 'MCPC', 'MPRC', 'MUWC', 'MCOC', 'MCOCS', 'MPRCC', 'MUWCC'];
	// Third party policy prefixes
	const thirdPartyPrefixes = ['MPCTP', 'MPCTPS', 'MCOTP', 'MCOTPS', 'MCPTP'];

	const maxFileSize = 20 * 1024 * 1024; // 20MB

	const certificateRequirement = {
		id: 'certificateOfFitness',
		label: 'Certificate of Fitness',
		accept: ['image/*', '.pdf'],
		mandatory: true,
		minFiles: 1,
		maxFileSize,
		description: 'Valid certificate of fitness for your vehicle',
		narrativeDescription: 'Kindly Submit  Motor Vehicle Fitness And Registration Certificates At Renewal.'
	};

	const valuationRequirement = {
		id: 'valuationReport',
		label: 'Valuation Report',
		accept: ['image/*', '.pdf'],
		mandatory: true,
		minFiles: 1,
		maxFileSize,
		description: 'Approved valuation report with accompanying imagery or invoice details', 
		narrativeDescription: 'Kindly Submit a Valuation Report'
	};

	const approvedValuationFreshRequirement = {
		id: 'approvedValuationReport60Days',
		label: 'Approved Valuation Report NOT older than 60 days',
		accept: ['image/*', '.pdf'],
		mandatory: true,
		minFiles: 1,
		maxFileSize,
		description: 'Approved valuation report issued within the last 60 days',
		narrativeDescription: 'Kindly Submit a Valuation Report'
	};

	const vehicleRegistrationRequirement = {
		id: 'vehicleRegistration',
		label: 'Motor Vehicle Registration',
		accept: ['image/*', '.pdf'],
		mandatory: false,
		minFiles: 1,
		maxFileSize,
		description: 'Vehicle registration document',
		narrativeDescription: 'Kindly Submit  Motor Vehicle Fitness And Registration Certificates At Renewal.'
	};

	const motorVehicleTitleRequirement = {
		id: 'motorVehicleTitle',
		label: 'Motor Vehicle Title (Back and Front)',
		accept: ['image/*', '.pdf'],
		mandatory: false,
		minFiles: 1,
		maxFiles: 2,
		maxFileSize,
		description: 'Both back and front of the motor vehicle title',
		narrativeDescription: 'Kindly submit a copy of the Motor Vehicle Title in the name of the previous owner'
	};

	const motorVehicleTitlePreviousOwnerRequirement = {
		...motorVehicleTitleRequirement,
		id: 'motorVehicleTitlePreviousOwner',
		label: 'Motor Vehicle Title from the previous owner (back & front)',
		mandatory: true,
		narrativeDescription: 'Kindly submit a copy of the Motor Vehicle Title in the name of the previous owner'
	};

	const vehiclePhotographsRequirement = {
		id: 'vehiclePhotographs',
		label: 'Photograph of Vehicle (all sections including glass & windows)',
		accept: ['image/*', '.pdf'],
		mandatory: true,
		minFiles: 1,
		maxFileSize,
		description: 'Comprehensive photographs of the vehicle, covering all sections including glass and windows',
		narrativeDescription: 'Kindly submit photographs of the vehicle to include all panels and glass (including windows and sunroof).'
	};

	const vehiclePhotographRequirement = {
		id: 'vehiclePhotograph',
		label: 'Photograph of Vehicle',
		accept: ['image/*', '.pdf'],
		mandatory: true,
		minFiles: 1,
		maxFileSize,
		description: 'Photograph of the vehicle',
		narrativeDescription: 'Kindly submit photographs of the vehicle to include all panels and glass (including windows and sunroof).'
	};

	const vehiclePhotographWithArticleRequirement = {
		...vehiclePhotographRequirement,
		id: 'vehiclePhotographWithArticle',
		label: 'Photograph of the Vehicle',
		narrativeDescription: 'Kindly submit photographs of the vehicle to include all panels and glass (including windows and sunroof).'
	};

	const proformaInvoiceRequirement = {
		id: 'proformaInvoiceAllPages',
		label: 'Proforma Invoice [all pages]',
		accept: ['image/*', '.pdf'],
		mandatory: true,
		minFiles: 1,
		maxFileSize,
		description: 'All pages of the proforma invoice from the dealer',
		narrativeDescription: 'Kindly submit a copy of the Proforma Invoice'
	};

	const vehicleImportEntryFormRequirement = {
		id: 'vehicleImportEntryForm',
		label: 'Vehicle Import Entry Form',
		accept: ['image/*', '.pdf'],
		mandatory: true,
		minFiles: 1,
		maxFileSize,
		description: 'Customs import entry form for the vehicle', 
		narrativeDescription: 'Kindly submit a copy of the Motor Vehicle Import Entry'
	};

	const mortgageeLetterRequirement = {
		id: 'mortgageeLetter',
		label: 'Mortgagee Letter',
		accept: ['image/*', '.pdf'],
		mandatory: false,
		minFiles: 1,
		maxFileSize,
		description: 'Letter from mortgagee'
	};

	let vehicleRequirements = [];
	let optionalRequirements = [];

	const rawPurchaseLocation = activeRisk?.vehicle_purchase_location ??
		activeRisk?.purchase_location ??
		activeRisk?.vehiclePurchaseLocation ??
		activeRisk?.vehicle_purchaseLocation ??
		'';

	const purchaseLocation = typeof rawPurchaseLocation === 'string' ? rawPurchaseLocation.trim() : '';
	const purchaseLocationKey = purchaseLocation.toLowerCase();

	if (comprehensivePrefixes.includes(policyPrefix)) {
		if (!isRegisteredInName) {
			if (purchaseLocationKey === 'local new car dealership') {
				vehicleRequirements = [
					proformaInvoiceRequirement,
					certificateRequirement
				];
				optionalRequirements = hasMortgagee ? [mortgageeLetterRequirement] : [];
			} else if (purchaseLocationKey === 'pre-owned (already registered in jamaica)') {
				vehicleRequirements = [
					certificateRequirement,
					motorVehicleTitlePreviousOwnerRequirement,
					approvedValuationFreshRequirement,
					vehiclePhotographRequirement
				];
				optionalRequirements = hasMortgagee ? [mortgageeLetterRequirement] : [];
			} else if (purchaseLocationKey === 'used car dealer with vehicle not yet registered in jamaica') {
				vehicleRequirements = [
					certificateRequirement,
					vehicleImportEntryFormRequirement,
					approvedValuationFreshRequirement,
					vehiclePhotographWithArticleRequirement
				];
				optionalRequirements = hasMortgagee ? [mortgageeLetterRequirement] : [];
			} else {
				vehicleRequirements = [
					certificateRequirement,
					valuationRequirement
				];

				optionalRequirements = [
					vehicleRegistrationRequirement
				];

				if (hasMortgagee) {
					optionalRequirements.push(mortgageeLetterRequirement);
				} else {
					optionalRequirements.push(motorVehicleTitleRequirement);
				}
			}
		} else if (hasMortgagee && isRegisteredInName) {
			vehicleRequirements = [
				certificateRequirement,
				{ ...vehicleRegistrationRequirement, mandatory: true },
				valuationRequirement,
				vehiclePhotographsRequirement
			];
			optionalRequirements = [];
		} else if (!hasMortgagee && isRegisteredInName) {
			vehicleRequirements = [
				certificateRequirement,
				{ ...vehicleRegistrationRequirement, mandatory: true },
				{ ...motorVehicleTitleRequirement, mandatory: true },
				valuationRequirement,
				vehiclePhotographsRequirement
			];
			optionalRequirements = [];
		} else {
			vehicleRequirements = [
				certificateRequirement,
				valuationRequirement
			];

			optionalRequirements = [
				vehicleRegistrationRequirement
			];

			if (hasMortgagee) {
				optionalRequirements.push(mortgageeLetterRequirement);
			} else {
				optionalRequirements.push(motorVehicleTitleRequirement);
			}
		}
	} else if (thirdPartyPrefixes.includes(policyPrefix)) {
		if (isRegisteredInName) {
			vehicleRequirements = [
				certificateRequirement,
				{ ...vehicleRegistrationRequirement, mandatory: true },
				{ ...motorVehicleTitleRequirement, mandatory: true }
			];
			optionalRequirements = [];
		} else {
			vehicleRequirements = [
				certificateRequirement,
				{ ...motorVehicleTitleRequirement, mandatory: true }
			];

			optionalRequirements = [
				vehicleRegistrationRequirement
			];
		}

			// 2035 - Kindly Submit  Motor Vehicle Fitness And Registration Certificates At Renewal.
					// 2041 - Kindly Submit a Valuation Report
					// 2075 - Kindly submit photographs of the vehicle to include all panels and glass (including windows and sunroof).
					// 2080 - Kindly submit a copy of the Motor Vehicle Title in the name of the previous owner 
					// 2081 - Kindly submit a copy of the Proforma Invoice 
					// 2082 - Kindly submit a copy of the Motor Vehicle Import Entry 
	}

	return {
		personal: [],
		vehicle: vehicleRequirements,
		optional: optionalRequirements
	};
};

export const {
	setSideBarInfo,
	setProgressLine,
	setGlobalNarratives,
	setMissingDocuments,
	setPreviousNavigationLink,
	setPayBalanceUserProgress,
	setNotification, 
	setClearNotificationStatus,
	login,
	logout,
	setToken,
	setRefreshToken,
	setStickyNavHeaderVisible,
	setRedirectTarget,
	setUserFormData,
	setCurrentUser,
	setPolicyHolders,
	setBreakdown,
	setUserId,
	setPolicies,
	setActivePolicy,
	setOldPolicy,
	setActivePolicies,
	setActiveRisk,
	setRenewablePolicies,
	setCoverNotePolicies,
	setAuthFormInfo,
	setHalfPayment,
	setPaymentPlans,
	setQuoteResponse,
	setExtendablePoliciesFetched,
	setPaymentType,
	setValuationStatus,
	setOtherCalculations,
	setExtendablePolicies,
	setPersistUser,
	setTransactionType,
	setPolicyPaymentPlan,
	addPromoCode,
	removePromoCodeByIndex,
	setMissingUserData,
	setNoLossDeclarationStatus,
	setHasMotorPolicies,
	setHasHomePolicies,
	setHasLiabilitiesPolicies,
	setSelectedSession,
	setIsOtherDeviceLogin,
} = authSlice.actions;
export default authSlice.reducer;
